/**
 * Pure equivalence checks for auth state updates.
 *
 * Used by AuthContext.onAuthStateChange to keep the `user`/`session` state
 * identities stable when a SIGNED_IN/TOKEN_REFRESHED event carries no material
 * change. Every auth event delivers freshly-built objects (re-broadcasts from
 * same-origin iframes included), and replacing state identity re-runs every
 * `[user]`/`[session]` effect in the app — which can unmount/remount whole
 * pages. Extracted so the logic is testable without rendering AuthProvider.
 */
import type { User, Session } from '@supabase/supabase-js';
import type { ExtendedUser } from '@/types/auth';

// Order-sensitive shallow compare; roles come from one JSONB column whose
// order is stable for an unchanged row.
const sameRoles = (a?: string[], b?: string[]): boolean =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((role, i) => role === b[i]);

export const isEquivalentExtendedUser = (prev: User | null, next: User): boolean => {
  if (!prev) {
    return false;
  }
  const p = prev as ExtendedUser;
  const n = next as ExtendedUser;
  return (
    p.id === n.id &&
    p.email === n.email &&
    p.organization_id === n.organization_id &&
    p.employment_status === n.employment_status &&
    p.firstName === n.firstName &&
    p.lastName === n.lastName &&
    p.fullName === n.fullName &&
    sameRoles(p.roles, n.roles)
  );
};

export const isEquivalentSession = (prev: Session | null, next: Session): boolean =>
  !!prev &&
  prev.access_token === next.access_token &&
  prev.expires_at === next.expires_at &&
  prev.user?.id === next.user?.id;
